import { ROLES } from './roles'
import { ACTIONS } from './actions'
import type { Role } from './roles'
import type { Action } from './actions'

/**
 * Permission Matrix
 *
 * This is the baseline permission matrix for the Backoffice App.
 * Tenant-scoped role permissions can override these defaults when loaded from the backend.
 *
 * To modify permissions:
 * 1. Update this matrix
 * 2. Update the documentation in docs/cloud/backoffice-app-permissions.md
 * 3. Test with affected user roles
 *
 * Role hierarchy (implicit, not enforced):
 * SUPER_ADMIN > TENANT_ADMIN > MANAGER > ASSISTANT
 */
export const ROLE_PERMISSIONS: Record<Role, Set<Action>> = {
  /**
   * SUPER_ADMIN: System administrator
   * Has all permissions in the system
   */
  [ROLES.SUPER_ADMIN]: new Set(Object.values(ACTIONS)),

  /**
   * TENANT_ADMIN: Tenant-level administrator
   * Can manage tenant-wide operations including product deletion and user access
   */
  [ROLES.TENANT_ADMIN]: new Set([
    ACTIONS.OVERVIEW_VIEW,
    ACTIONS.TRANSACTION_VIEW,
    ACTIONS.PRODUCT_VIEW,
    ACTIONS.PRODUCT_CREATE,
    ACTIONS.PRODUCT_EDIT,
    ACTIONS.PRODUCT_DELETE, // ✅ Can delete products
    ACTIONS.ASSIGNMENT_MANAGE,
    ACTIONS.PRICE_VIEW,
    ACTIONS.PRICE_EDIT,
    ACTIONS.BUNDLE_PUBLISH,
    ACTIONS.EDGE_VIEW,
    ACTIONS.EDGE_PAIRING_CREATE,
    ACTIONS.RESTAURANT_VIEW,
    ACTIONS.RESTAURANT_MANAGE,
    ACTIONS.RESTAURANT_SCHEDULING_MANAGE,
    ACTIONS.RESTAURANT_EDGE_SETTINGS_MANAGE,
    ACTIONS.RESTAURANT_ESL_MANAGE,
    ACTIONS.RESTAURANT_BRANDING_MANAGE,
    ACTIONS.POS_LAYOUT_VIEW,
    ACTIONS.POS_LAYOUT_EDIT,
    ACTIONS.STAFF_VIEW,
    ACTIONS.STAFF_MANAGE,
    ACTIONS.VOUCHER_VIEW,
    ACTIONS.VOUCHER_MANAGE,
    ACTIONS.POS_VOID_REASON_VIEW,
    ACTIONS.POS_VOID_REASON_MANAGE,
    ACTIONS.ENTNAHME_REASON_VIEW,
    ACTIONS.ENTNAHME_REASON_MANAGE,
    ACTIONS.INVENTORY_VIEW,
    ACTIONS.INVENTORY_RECEIVE,
    ACTIONS.INVENTORY_COUNT,
    ACTIONS.INVENTORY_ADJUST,
    ACTIONS.INVENTORY_TRANSFER,
    ACTIONS.INVENTORY_COUNT_SESSION,
    ACTIONS.INVENTORY_DELIVERY,
    ACTIONS.PURCHASE_ORDER_VIEW,
    ACTIONS.PURCHASE_ORDER_ORDER,
    ACTIONS.CATALOG_IMPORT,
    ACTIONS.PRICING_IMPORT,
    ACTIONS.CATEGORY_VIEW,
    ACTIONS.CATEGORY_CREATE,
    ACTIONS.MENU_VIEW,
    ACTIONS.MENU_CREATE,
    ACTIONS.MENU_EDIT,
    ACTIONS.MENU_DELETE,
    ACTIONS.MENU_PUBLISH,
    ACTIONS.USER_MANAGE,
    ACTIONS.ROLE_PERMISSION_MANAGE,
    ACTIONS.CUSTOMER_VIEW,
    ACTIONS.CUSTOMER_MANAGE,
    ACTIONS.INTEGRATIONS_MANAGE, // ✅ API clients (Integrationen) — tenant admins only
  ]),

  /**
   * MANAGER: Restaurant manager
   * Can manage restaurant-level operations including pairing codes and bundles
   * Cannot delete products or manage users
   */
  [ROLES.MANAGER]: new Set([
    ACTIONS.OVERVIEW_VIEW,
    ACTIONS.TRANSACTION_VIEW,
    ACTIONS.PRODUCT_VIEW,
    ACTIONS.PRODUCT_CREATE,
    ACTIONS.PRODUCT_EDIT,
    // ❌ Cannot delete products
    ACTIONS.ASSIGNMENT_MANAGE,
    ACTIONS.PRICE_VIEW,
    ACTIONS.PRICE_EDIT,
    ACTIONS.BUNDLE_PUBLISH,
    ACTIONS.EDGE_VIEW,
    ACTIONS.EDGE_PAIRING_CREATE, // ✅ Can create pairing codes
    ACTIONS.RESTAURANT_VIEW,
    ACTIONS.RESTAURANT_MANAGE,
    ACTIONS.RESTAURANT_SCHEDULING_MANAGE,
    ACTIONS.RESTAURANT_EDGE_SETTINGS_MANAGE,
    ACTIONS.RESTAURANT_ESL_MANAGE,
    ACTIONS.RESTAURANT_BRANDING_MANAGE,
    ACTIONS.POS_LAYOUT_VIEW,
    ACTIONS.POS_LAYOUT_EDIT,
    ACTIONS.STAFF_VIEW,
    ACTIONS.STAFF_MANAGE,
    ACTIONS.VOUCHER_VIEW,
    ACTIONS.VOUCHER_MANAGE,
    ACTIONS.POS_VOID_REASON_VIEW,
    ACTIONS.POS_VOID_REASON_MANAGE,
    ACTIONS.ENTNAHME_REASON_VIEW,
    ACTIONS.ENTNAHME_REASON_MANAGE,
    ACTIONS.INVENTORY_VIEW,
    ACTIONS.INVENTORY_RECEIVE,
    ACTIONS.INVENTORY_COUNT,
    ACTIONS.INVENTORY_ADJUST,
    ACTIONS.INVENTORY_TRANSFER,
    ACTIONS.INVENTORY_COUNT_SESSION,
    ACTIONS.INVENTORY_DELIVERY,
    ACTIONS.PURCHASE_ORDER_VIEW,
    ACTIONS.PURCHASE_ORDER_ORDER,
    ACTIONS.CATALOG_IMPORT,
    ACTIONS.PRICING_IMPORT,
    ACTIONS.CATEGORY_VIEW,
    ACTIONS.CATEGORY_CREATE,
    ACTIONS.MENU_VIEW,
    ACTIONS.MENU_CREATE,
    ACTIONS.MENU_EDIT,
    ACTIONS.MENU_DELETE,
    ACTIONS.MENU_PUBLISH,
    ACTIONS.CUSTOMER_VIEW,
    ACTIONS.CUSTOMER_MANAGE,
  ]),

  /**
   * ASSISTANT: General staff member
   * Can perform basic product and price management
   * Cannot delete products, publish bundles, create pairing codes, or manage users
   */
  [ROLES.ASSISTANT]: new Set([
    ACTIONS.OVERVIEW_VIEW,
    ACTIONS.TRANSACTION_VIEW,
    ACTIONS.PRODUCT_VIEW,
    ACTIONS.PRODUCT_CREATE,
    ACTIONS.PRODUCT_EDIT,
    // ❌ Cannot delete products
    ACTIONS.ASSIGNMENT_MANAGE,
    ACTIONS.PRICE_VIEW,
    ACTIONS.PRICE_EDIT,
    // ❌ Cannot publish bundles
    ACTIONS.EDGE_VIEW,
    // ❌ Cannot create pairing codes
    ACTIONS.RESTAURANT_VIEW,
    ACTIONS.STAFF_VIEW,
    ACTIONS.VOUCHER_VIEW,
    ACTIONS.POS_VOID_REASON_VIEW,
    ACTIONS.ENTNAHME_REASON_VIEW,
    ACTIONS.INVENTORY_VIEW,
    ACTIONS.INVENTORY_RECEIVE,
    ACTIONS.INVENTORY_COUNT_SESSION, // ✅ Can run physical count sessions
    ACTIONS.INVENTORY_DELIVERY, // ✅ Can announce, check and book deliveries
    ACTIONS.CATALOG_IMPORT,
    ACTIONS.PRICING_IMPORT,
    ACTIONS.CATEGORY_VIEW, // ✅ Can view categories
    // ❌ Cannot create categories
    ACTIONS.MENU_VIEW, // ✅ Can view menus
    // ❌ Cannot create/edit/delete/publish menus
    ACTIONS.CUSTOMER_VIEW, // ✅ Can view customers
    // ❌ Cannot manage customers
  ]),
}

/**
 * Check if any of the user's roles allow the specified action
 *
 * @param userRoles - Array of role strings from the user's JWT token
 * @param action - The action to check permission for
 * @returns true if the user has permission, false otherwise
 *
 * @example
 * const canDelete = canPerformAction(user.roles, ACTIONS.PRODUCT_DELETE)
 * if (canDelete) {
 *   // Show delete button
 * }
 */
export type RolePermissionsMap = Record<string, Set<Action> | undefined>

export function canPerformAction(
  userRoles: Array<string>,
  action: Action,
  rolePermissions?: RolePermissionsMap,
): boolean {
  const source: RolePermissionsMap = rolePermissions ?? ROLE_PERMISSIONS
  return userRoles.some((role) => source[role]?.has(action) ?? false)
}
